import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  IconButton,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  useTheme,
  Skeleton,
  Button,
  Tooltip,
  Badge
} from '@mui/material';
import {
  Notifications,
  Warning,
  Info,
  Error as ErrorIcon,
  CheckCircle,
  Close,
  AccessTime,
  Cake,
  WorkOutline,
  EventNote,
  VisibilityOff,
  ArrowForward
} from '@mui/icons-material';

interface Alert {
  id: number;
  alert_type: string;
  title: string;
  description?: string;
  priority: 'low' | 'normal' | 'high' | 'urgent';
  due_date?: string;
  is_read: boolean;
  created_at: string;
  action_url?: string;
}

interface SmartAlertsWidgetProps {
  alerts: Alert[];
  loading?: boolean;
  unreadCount?: number;
  onDismiss?: (id: number) => void;
  onMarkAsRead?: (id: number) => void;
  onViewAll?: () => void;
  onAlertClick?: (alert: Alert) => void;
  maxItems?: number;
}

const SmartAlertsWidget: React.FC<SmartAlertsWidgetProps> = ({
  alerts,
  loading = false,
  unreadCount = 0,
  onDismiss,
  onMarkAsRead,
  onViewAll,
  onAlertClick,
  maxItems = 5
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const getPriorityColor = (priority: Alert['priority']) => {
    switch (priority) {
      case 'urgent':
        return '#F44336';
      case 'high':
        return '#FF9800';
      case 'normal':
        return '#2196F3';
      case 'low':
        return '#4CAF50';
      default:
        return theme.palette.text.secondary;
    }
  };

  const getPriorityIcon = (priority: Alert['priority']) => {
    switch (priority) {
      case 'urgent':
        return <ErrorIcon sx={{ color: '#F44336' }} />;
      case 'high':
        return <Warning sx={{ color: '#FF9800' }} />;
      case 'normal':
        return <Info sx={{ color: '#2196F3' }} />;
      case 'low':
        return <CheckCircle sx={{ color: '#4CAF50' }} />;
      default:
        return <Notifications />;
    }
  };

  const getAlertTypeIcon = (alertType: string) => {
    switch (alertType) {
      case 'birthday':
        return <Cake sx={{ color: '#E91E63' }} />;
      case 'work_anniversary':
        return <WorkOutline sx={{ color: '#9C27B0' }} />;
      case 'probation_ending':
        return <AccessTime sx={{ color: '#FF5722' }} />;
      case 'leave_balance_low':
        return <EventNote sx={{ color: '#FF9800' }} />;
      case 'pending_approval':
        return <AccessTime sx={{ color: '#2196F3' }} />;
      default:
        return <Notifications />;
    }
  };

  const formatTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (diffInSeconds < 60) return 'Just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
    if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d ago`;
    return date.toLocaleDateString();
  };

  const displayedAlerts = alerts.slice(0, maxItems);

  return (
    <Card
      sx={{
        backgroundColor: isDark ? 'rgba(255, 255, 255, 0.05)' : theme.palette.background.paper,
        borderRadius: 2,
        border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.1)' : theme.palette.divider}`,
        height: '100%',
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      <CardContent sx={{ p: 0, flex: 1, display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: 2,
            borderBottom: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.1)' : theme.palette.divider}`
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Badge badgeContent={unreadCount} color="error" max={99}>
              <Notifications sx={{ color: theme.palette.primary.main }} />
            </Badge>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Smart Alerts
            </Typography>
          </Box>
          {onViewAll && (
            <Button
              size="small"
              endIcon={<ArrowForward />}
              onClick={onViewAll}
              sx={{ textTransform: 'none' }}
            >
              View All
            </Button>
          )}
        </Box>

        {/* Alerts List */}
        <Box sx={{ flex: 1, overflow: 'auto' }}>
          {loading ? (
            <List sx={{ p: 1 }}>
              {[1, 2, 3].map(i => (
                <ListItem key={i} sx={{ py: 1.5 }}>
                  <ListItemIcon>
                    <Skeleton variant="circular" width={40} height={40} />
                  </ListItemIcon>
                  <ListItemText
                    primary={<Skeleton variant="text" width="70%" />}
                    secondary={<Skeleton variant="text" width="40%" />}
                  />
                </ListItem>
              ))}
            </List>
          ) : displayedAlerts.length === 0 ? (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                py: 6,
                px: 2
              }}
            >
              <CheckCircle sx={{ fontSize: 48, color: '#4CAF50', mb: 1 }} />
              <Typography variant="body1" color="text.secondary">
                All caught up!
              </Typography>
              <Typography variant="body2" color="text.disabled">
                No pending alerts
              </Typography>
            </Box>
          ) : (
            <List sx={{ p: 0 }}>
              {displayedAlerts.map((alert, index) => (
                <ListItem
                  key={alert.id}
                  sx={{
                    py: 1.5,
                    px: 2,
                    borderBottom: index < displayedAlerts.length - 1
                      ? `1px solid ${isDark ? 'rgba(255, 255, 255, 0.05)' : theme.palette.divider}`
                      : 'none',
                    backgroundColor: !alert.is_read
                      ? isDark ? 'rgba(33, 150, 243, 0.1)' : 'rgba(33, 150, 243, 0.05)'
                      : 'transparent',
                    cursor: onAlertClick ? 'pointer' : 'default',
                    '&:hover': {
                      backgroundColor: isDark ? 'rgba(255, 255, 255, 0.05)' : theme.palette.action.hover
                    }
                  }}
                  onClick={() => onAlertClick?.(alert)}
                >
                  <ListItemIcon sx={{ minWidth: 44 }}>
                    <Box
                      sx={{
                        width: 36,
                        height: 36,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: `${getPriorityColor(alert.priority)}15`,
                        border: `1px solid ${getPriorityColor(alert.priority)}30`
                      }}
                    >
                      {getAlertTypeIcon(alert.alert_type)}
                    </Box>
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: alert.is_read ? 400 : 600,
                            color: theme.palette.text.primary,
                            flex: 1
                          }}
                          noWrap
                        >
                          {alert.title}
                        </Typography>
                        {alert.priority === 'urgent' && (
                          <Chip
                            label="Urgent"
                            size="small"
                            sx={{
                              height: 20,
                              fontSize: '0.65rem',
                              fontWeight: 600,
                              backgroundColor: '#F4433620',
                              color: '#F44336',
                              border: '1px solid #F4433640'
                            }}
                          />
                        )}
                      </Box>
                    }
                    secondary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.25 }}>
                        <Typography variant="caption" color="text.secondary" noWrap sx={{ flex: 1 }}>
                          {alert.description || formatTimeAgo(alert.created_at)}
                        </Typography>
                        {alert.due_date && (
                          <Chip
                            size="small"
                            icon={<AccessTime sx={{ fontSize: '0.75rem !important' }} />}
                            label={new Date(alert.due_date).toLocaleDateString()}
                            sx={{
                              height: 18,
                              fontSize: '0.65rem',
                              '& .MuiChip-icon': { ml: 0.5 }
                            }}
                          />
                        )}
                      </Box>
                    }
                  />
                  <ListItemSecondaryAction>
                    <Tooltip title="Dismiss">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          onDismiss?.(alert.id);
                        }}
                        sx={{
                          opacity: 0.5,
                          '&:hover': { opacity: 1 }
                        }}
                      >
                        <Close fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          )}
        </Box>

        {/* Footer - if more alerts exist */}
        {alerts.length > maxItems && (
          <Box
            sx={{
              p: 1.5,
              textAlign: 'center',
              borderTop: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.1)' : theme.palette.divider}`
            }}
          >
            <Typography variant="caption" color="text.secondary">
              +{alerts.length - maxItems} more alerts
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default SmartAlertsWidget;
